import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Box,
  Text,
  Progress,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useUploadContent, useContentQuota } from '../hooks/useCustomContent';
import { TagsInput } from './TagsInput';

const ACCEPT = { 'audio/*': ['.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a'] };

interface UploadContentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UploadContentModal({ isOpen, onClose }: UploadContentModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const upload = useUploadContent();
  const { data: quota } = useContentQuota();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback((accepted: File[]) => {
    const f = accepted[0];
    if (f) {
      setFile(f);
      setTitle((prev) => prev || f.name.replace(/\.[^.]+$/, ''));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    multiple: false,
    disabled: upload.isPending,
  });

  const reset = () => {
    setFile(null);
    setTitle('');
    setTags([]);
    setProgress(0);
  };

  const handleClose = () => {
    if (upload.isPending) return;
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    setProgress(0);
    try {
      await upload.mutateAsync({ file, title: title.trim(), tags, onProgress: setProgress });
      toast({ status: 'success', title: t('customContent.uploaded'), duration: 2500 });
      reset();
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        title: t('common.error', 'Something went wrong'),
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const quotaFull = quota ? quota.currentClips >= quota.maxClips : false;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('customContent.upload')}</ModalHeader>
        <ModalCloseButton isDisabled={upload.isPending} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {quota && (
              <Text fontSize="sm" color={quotaFull ? 'red.500' : 'gray.500'}>
                {t('customContent.quota', { used: quota.currentClips, max: quota.maxClips })}
              </Text>
            )}

            <Box
              {...getRootProps()}
              borderWidth="2px"
              borderStyle="dashed"
              borderColor={isDragActive ? 'brand.400' : 'gray.300'}
              borderRadius="lg"
              p={6}
              textAlign="center"
              cursor="pointer"
              bg={isDragActive ? 'brand.50' : 'gray.50'}
              transition="all 0.15s"
            >
              <input {...getInputProps()} />
              <Text color="gray.600">{file ? file.name : t('customContent.dropHint')}</Text>
              {file && (
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </Text>
              )}
            </Box>

            <FormControl isRequired>
              <FormLabel>{t('customContent.clipTitle')}</FormLabel>
              <Input
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                isDisabled={upload.isPending}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t('customContent.tags')}</FormLabel>
              <TagsInput value={tags} onChange={setTags} placeholder={t('customContent.addTag')} />
            </FormControl>

            {upload.isPending && (
              <Progress value={progress} size="sm" colorScheme="brand" borderRadius="full" hasStripe isAnimated />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={upload.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleUpload}
            isLoading={upload.isPending}
            isDisabled={!file || !title.trim() || quotaFull}
          >
            {t('customContent.upload')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default UploadContentModal;
